// src/V2/components/common/ImageCarousel.tsx
import React, { useEffect, useState } from "react";
import { IconButton, Box } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useSwipeable } from "react-swipeable";

type Props = {
    images: string[];
    startIndex?: number;
    onIndexChange?: (idx: number) => void;
    height?: number | string; // ej: 180 o "200px"
    rounded?: boolean;
};

const ImageCarousel: React.FC<Props> = ({ images, startIndex = 0, onIndexChange, height = 180, rounded = true }) => {
    const [index, setIndex] = useState(startIndex);

    useEffect(() => { setIndex(startIndex); }, [startIndex]);

    const hasMany = images.length > 1;
    const go = (dir: 1 | -1) => {
        if (!images.length) return;
        const next = (index + dir + images.length) % images.length;
        setIndex(next);
        onIndexChange?.(next);
    };

    const handlers = useSwipeable({
        onSwipedLeft: () => go(1),
        onSwipedRight: () => go(-1),
        trackMouse: true,
        preventScrollOnSwipe: true,
    });

    const current = images[index] ?? "";

    const radius = rounded ? 10 : 0;

    return (
        <Box {...handlers} sx={{
            position: "relative",
            width: "100%",
            height,
            overflow: "hidden",
            borderRadius: radius,
            backgroundColor: "#f2f2f2"
        }}>
            {/* Imagen */}
            {current && (
                <img
                    src={current}
                    alt={`img-${index}`}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        userSelect: "none"
                    }}
                    draggable={false}
                />
            )}

            {/* Flechas */}
            {hasMany && (
                <>
                    {/* Zona izquierda */}
                    <Box
                        onClick={(e) => { e.stopPropagation(); go(-1); }}
                        sx={{
                            position: "absolute",
                            top: 0,
                            bottom: 0,
                            left: 0,
                            width: "18%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            pl: 1,
                            cursor: "pointer",
                            zIndex: 2,
                        }}
                        >
                        <IconButton
                            aria-label="Anterior"
                            size="medium"
                            sx={{
                                color: "#3d3d3dff",
                                "& .MuiSvgIcon-root": { fontSize: 32 },
                            }}
                        >
                            <ChevronLeft />
                        </IconButton>
                    </Box>

                    {/* Zona derecha */}
                    <Box
                        onClick={(e) => { e.stopPropagation(); go(1); }}
                        sx={{
                            position: "absolute",
                            top: 0,
                            bottom: 0,
                            right: 0,
                            width: "18%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            pr: 1,
                            cursor: "pointer",
                            zIndex: 2,
                        }}
                        >
                        <IconButton
                            aria-label="Siguiente"
                            size="medium"
                            sx={{
                                color: "#3d3d3dff",
                                "& .MuiSvgIcon-root": { fontSize: 32 },
                            }}
                        >
                            <ChevronRight />
                        </IconButton>
                    </Box>
                </>
            )}


            {/* Paginación (puntos) */}
            {hasMany && (
                <Box
                    sx={{
                    position: "absolute",
                    bottom: 6,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    gap: "6px",
                    zIndex: 3,
                    }}
                >
                    {images.map((_, i) => (
                        <Box
                            key={i}
                            sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: i === index ? "#3d3d3dff" : "rgba(53, 53, 53, 0.55)", // oscuro
                            transition: "background-color .15s ease",
                            "&:hover": { backgroundColor: "#3d3d3dff" }, // al pasar, negro
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default ImageCarousel;
